import { format } from 'date-fns';
import { MessageSquare } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface ConversationHistoryMessage {
  id: string;
  content: string;
  is_inbound: boolean;
  source_timestamp: string | null;
}

interface ConversationHistoryProps {
  messages: ConversationHistoryMessage[];
  className?: string;
}

export const ConversationHistory = ({
  messages,
  className,
}: ConversationHistoryProps) => {
  if (messages.length === 0) {
    return (
      <div
        className={cn(
          'rounded-lg border bg-zinc-50 p-4 h-full flex flex-col items-center justify-center',
          className,
        )}
      >
        <MessageSquare className="h-8 w-8 text-zinc-300 mb-2" />
        <p className="text-sm text-zinc-500 text-center">
          No previous conversation with this client
        </p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'rounded-lg border bg-zinc-50 flex flex-col h-full',
        className,
      )}
    >
      <div className="flex-shrink-0 px-4 py-2 border-b bg-zinc-100/50">
        <h4 className="text-xs font-medium text-zinc-700 uppercase tracking-wide">
          Conversation History
        </h4>
        <p className="text-[10px] text-zinc-500 mt-0.5">
          {messages.length} message{messages.length !== 1 ? 's' : ''}
        </p>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {messages.map((message) => (
          <div
            key={message.id}
            className={cn(
              'rounded-lg px-3 py-2 text-sm',
              message.is_inbound ? 'bg-white border mr-4' : 'bg-indigo-50 ml-4',
            )}
          >
            <div className="flex items-center gap-2 mb-1">
              <span
                className={cn(
                  'text-[10px] font-medium',
                  message.is_inbound ? 'text-zinc-500' : 'text-indigo-600',
                )}
              >
                {message.is_inbound ? '← Inbound' : '→ Outbound'}
              </span>
              <span className="text-[10px] text-zinc-400">
                {message.source_timestamp
                  ? format(new Date(message.source_timestamp), 'MMM d, h:mm a')
                  : 'Unknown time'}
              </span>
            </div>
            <p className="text-zinc-800 whitespace-pre-wrap break-words text-xs">
              {message.content}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};
